import { useEffect, useState } from 'react';
import { CheckCircle, Coins, Loader2, Pencil, Phone, XCircle, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  useAiPricingTiers,
  useAiCreditsStatus,
  useAiCreditPacks,
  useAiCreditPaymentStatus,
  AI_CREDITS_STATUS_QUERY_KEY,
} from '@/hooks/queries/useAiCredits';
import { useInitiateCreditPurchase } from '@/hooks/mutations/useAiCreditMutations';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMethods, type PaymentMethodId } from '@/hooks/queries/usePaymentMethods';
import { PaymentMethodSelector } from '@/components/billing/PaymentMethodSelector';
import { CardPaymentForm, type CardDetails } from '@/components/billing/CardPaymentForm';
import type { AiPricingTier } from '@/types/ai-credits';

interface AiCreditsPurchaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Start in quick mode with pack selection grid */
  quick?: boolean;
  /** Pre-select a specific pack by credits amount */
  preselectedCredits?: number;
  /** Callback when purchase completes successfully */
  onPurchaseComplete?: () => void;
}

function formatFCFA(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function calculatePrice(credits: number, tiers: AiPricingTier[]): number {
  for (const tier of tiers) {
    if (credits >= tier.min && credits <= tier.max) {
      return credits * tier.price_per_credit;
    }
  }
  return credits * 20;
}

function getCurrentTier(credits: number, tiers: AiPricingTier[]): AiPricingTier | null {
  for (const tier of tiers) {
    if (credits >= tier.min && credits <= tier.max) {
      return tier;
    }
  }
  return null;
}

function QuickPurchaseContent({
  onClose,
  onSwitchToFull,
  preselectedCredits,
  onPurchaseComplete,
}: {
  onClose: () => void;
  onSwitchToFull: () => void;
  preselectedCredits?: number;
  onPurchaseComplete?: () => void;
}) {
  const { user } = useAuth();
  const { data: packs, isLoading: packsLoading } = useAiCreditPacks();
  const initiatePurchase = useInitiateCreditPurchase();
  const queryClient = useQueryClient();
  const { data: paymentMethods } = usePaymentMethods();

  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethodId>('mobile_money');
  const [card, setCard] = useState<CardDetails | null>(null);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone ?? '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const { data: paymentStatus } = useAiCreditPaymentStatus(transactionId);

  // Pre-select pack based on preselectedCredits or default to middle pack
  useEffect(() => {
    if (!packs || packs.length === 0 || selectedPackId !== null) return;
    if (preselectedCredits) {
      const match = packs.find((p) => p.credits === preselectedCredits);
      if (match) {
        setSelectedPackId(match.id);
        return;
      }
    }
    // Default: middle pack (index 1) or first
    const defaultPack = packs[Math.min(1, packs.length - 1)];
    if (defaultPack) setSelectedPackId(defaultPack.id);
  }, [packs, preselectedCredits, selectedPackId]);

  useEffect(() => {
    if (!paymentPending || !paymentStatus) return;
    if (paymentStatus.status === 'completed') {
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      const timer = setTimeout(() => {
        setPaymentPending(false);
        setTransactionId(null);
        onPurchaseComplete?.();
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (paymentStatus.status === 'failed') {
      setPaymentPending(false);
      setTransactionId(null);
    }
  }, [paymentPending, paymentStatus, queryClient, onClose, onPurchaseComplete]);

  const selectedPack = packs?.find((p) => p.id === selectedPackId);
  const isPhoneValid = phoneNumber.replace(/\s/g, '').length >= 8;
  const canPay = method === 'card' ? card !== null : isPhoneValid;

  async function handlePay() {
    if (!selectedPack || !canPay) return;
    try {
      const result = await initiatePurchase.mutateAsync(
        method === 'card' && card
          ? { credits: selectedPack.credits, payment_method: 'card', card }
          : { credits: selectedPack.credits, phone_number: phoneNumber.replace(/\s/g, '') },
      );
      if (method === 'card') {
        // 3-D Secure needs a full-page redirect (bank ACS pages break in
        // popups); return goes through /payments/return → result page.
        if (result.payment_url) {
          window.location.href = result.payment_url;
          return;
        }
        // Charged without 3-DS: poll the status (completed immediately).
        setPaymentPending(true);
        setTransactionId(result.transaction_id);
        return;
      }
      if (result.payment_url) {
        window.open(result.payment_url, '_blank');
        setPaymentPending(true);
        setTransactionId(result.transaction_id);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  if (packsLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="size-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Pack selection grid */}
      <div className="grid grid-cols-3 gap-2">
        {(packs ?? []).map((pack) => {
          const isSelected = pack.id === selectedPackId;
          const pricePerCredit = pack.credits > 0 ? Math.round(pack.price / pack.credits) : 0;
          return (
            <button
              key={pack.id}
              type="button"
              onClick={() => setSelectedPackId(pack.id)}
              className={cn(
                'rounded-xl border-2 p-3 text-center transition-all active:scale-[0.97]',
                isSelected
                  ? 'border-lime-500 bg-lime-50 dark:bg-lime-950 shadow-sm'
                  : 'border-neutral-200 dark:border-charcoal-700 hover:border-neutral-300 dark:hover:border-charcoal-600',
              )}
            >
              <p className="font-heading text-lg font-bold text-charcoal-900 dark:text-charcoal-100">
                {pack.credits}
              </p>
              <p className="text-[11px] text-neutral-500">crédits</p>
              <p className="mt-1 text-sm font-semibold text-charcoal-800 dark:text-charcoal-200">
                {formatFCFA(pack.price)} F
              </p>
              <p className="text-[10px] text-neutral-400">{pricePerCredit} F/cr.</p>
            </button>
          );
        })}
      </div>

      {/* Payment method */}
      <PaymentMethodSelector
        value={method}
        onChange={setMethod}
        amount={selectedPack?.price ?? 0}
        methods={paymentMethods}
        disabled={initiatePurchase.isPending || paymentPending}
      />

      {method === 'card' ? (
        <CardPaymentForm onChange={setCard} disabled={initiatePurchase.isPending} />
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-charcoal-700 dark:text-charcoal-300">
              Mobile Money
            </p>
            {!editingPhone && phoneNumber && (
              <button
                type="button"
                onClick={() => setEditingPhone(true)}
                className="flex items-center gap-1 text-[11px] text-primary hover:underline"
              >
                <Pencil className="size-3" />
                Modifier
              </button>
            )}
          </div>
          {editingPhone || !phoneNumber ? (
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="tel"
                placeholder="6XX XXX XXX"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={() => {
                  if (phoneNumber) setEditingPhone(false);
                }}
                autoFocus
                className="h-10 w-full rounded-md border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition-shadow focus:border-lime-400 focus:ring-2 focus:ring-lime-400/25 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-charcoal-100"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md bg-neutral-100 dark:bg-charcoal-800 px-3 py-2">
              <Phone className="size-3.5 text-neutral-500" />
              <span className="text-sm font-medium text-charcoal-800 dark:text-charcoal-200">
                {phoneNumber}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {initiatePurchase.isError && (
        <div className="rounded-md border border-error bg-error-light p-3 text-sm text-error-dark">
          Erreur lors du paiement. Veuillez réessayer.
        </div>
      )}

      {/* Payment pending state */}
      {paymentPending && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-700 dark:bg-charcoal-800">
          {paymentStatus?.status === 'completed' ? (
            <div className="flex items-center gap-2 text-sm font-medium text-lime-700">
              <CheckCircle className="size-4" />
              Paiement confirmé ! Crédits ajoutés.
            </div>
          ) : paymentStatus?.status === 'failed' ? (
            <div className="flex items-center gap-2 text-sm font-medium text-error-dark">
              <XCircle className="size-4" />
              Paiement échoué. Vous pouvez réessayer.
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Loader2 className="size-4 animate-spin text-neutral-400" />
              <div>
                <p className="text-sm font-medium text-charcoal-900">Paiement en cours…</p>
                <p className="text-xs text-neutral-500">
                  {method === 'card'
                    ? 'Vérification du paiement en cours…'
                    : "Complétez le paiement dans l'onglet ouvert."}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pay button */}
      {!paymentPending && (
        <>
          <button
            type="button"
            onClick={() => void handlePay()}
            disabled={!selectedPack || !canPay || initiatePurchase.isPending}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-lime-400 px-4 text-sm font-semibold text-charcoal-950 transition-colors hover:bg-lime-500 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/40 focus-visible:ring-offset-1"
          >
            {initiatePurchase.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Redirection…
              </>
            ) : (
              <>
                <Zap className="size-4" strokeWidth={2} aria-hidden />
                Payer {selectedPack ? `${formatFCFA(selectedPack.price)} FCFA` : ''}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onSwitchToFull}
            className="block w-full text-center text-xs text-primary hover:underline"
          >
            Montant personnalisé →
          </button>
        </>
      )}
    </div>
  );
}

function PurchaseContent({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { data: pricing, isLoading: pricingLoading } = useAiPricingTiers();
  const { data: status } = useAiCreditsStatus();
  const initiatePurchase = useInitiateCreditPurchase();
  const { data: paymentMethods } = usePaymentMethods();
  const [credits, setCredits] = useState(100);
  const [method, setMethod] = useState<PaymentMethodId>('mobile_money');
  const [card, setCard] = useState<CardDetails | null>(null);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone ?? '');
  const [confirming, setConfirming] = useState(false);

  const queryClient = useQueryClient();
  const [paymentPending, setPaymentPending] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const { data: paymentStatus } = useAiCreditPaymentStatus(transactionId);

  useEffect(() => {
    if (!paymentPending || !paymentStatus) return;
    if (paymentStatus.status === 'completed') {
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      // Auto-close after 2s
      const timer = setTimeout(() => {
        setPaymentPending(false);
        setTransactionId(null);
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (paymentStatus.status === 'failed') {
      setPaymentPending(false);
      setTransactionId(null);
    }
  }, [paymentPending, paymentStatus, queryClient, onClose]);

  const tiers = pricing?.tiers ?? [];
  const minCredits = pricing?.min_credits ?? 10;
  const maxCredits = pricing?.max_credits ?? 2000;
  const price = calculatePrice(credits, tiers);
  const currentTier = getCurrentTier(credits, tiers);
  const isPhoneValid = phoneNumber.replace(/\s/g, '').length >= 8;
  const canPay = method === 'card' ? card !== null : isPhoneValid;

  async function handleConfirm() {
    try {
      const result = await initiatePurchase.mutateAsync(
        method === 'card' && card
          ? { credits, payment_method: 'card', card }
          : { credits, phone_number: phoneNumber.replace(/\s/g, '') },
      );
      if (method === 'card') {
        // 3-D Secure needs a full-page redirect (bank ACS pages break in
        // popups); return goes through /payments/return → result page.
        if (result.payment_url) {
          window.location.href = result.payment_url;
          return;
        }
        // Charged without 3-DS: poll the status (completed immediately).
        setPaymentPending(true);
        setTransactionId(result.transaction_id);
        return;
      }
      if (result.payment_url) {
        window.open(result.payment_url, '_blank');
        setPaymentPending(true);
        setTransactionId(result.transaction_id);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  if (pricingLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="size-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4">
      {status?.balance !== undefined && (
        <p className="text-sm text-neutral-500">
          Solde actuel :{' '}
          <span className="font-semibold text-charcoal-900">{status.balance} crédits</span>
        </p>
      )}

      {/* Slider + Input */}
      <div className="space-y-3">
        <label htmlFor="credits-input" className="text-sm font-medium text-charcoal-900">
          Nombre de crédits
        </label>
        <input
          id="credits-input"
          type="number"
          min={minCredits}
          max={maxCredits}
          value={credits}
          onChange={(e) => {
            const val = Math.max(
              minCredits,
              Math.min(maxCredits, Number(e.target.value) || minCredits),
            );
            setCredits(val);
          }}
          className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-center font-heading text-xl font-bold text-slate-900 outline-none transition-shadow focus:border-lime-400 focus:ring-2 focus:ring-lime-400/25 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-charcoal-100"
        />
        <input
          type="range"
          min={minCredits}
          max={maxCredits}
          step={10}
          value={credits}
          onChange={(e) => setCredits(Number(e.target.value))}
          className="w-full cursor-pointer accent-lime-500"
        />
        <div className="flex justify-between text-[11px] text-neutral-400">
          <span>{minCredits}</span>
          <span>{maxCredits}</span>
        </div>
      </div>

      {/* Moyen de paiement */}
      <PaymentMethodSelector
        value={method}
        onChange={setMethod}
        amount={price}
        methods={paymentMethods}
        disabled={initiatePurchase.isPending || paymentPending}
      />

      {method === 'card' ? (
        <CardPaymentForm onChange={setCard} disabled={initiatePurchase.isPending} />
      ) : (
        <div className="space-y-2">
          <label htmlFor="phone-input" className="text-sm font-medium text-charcoal-900">
            Numéro de téléphone (Mobile Money)
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
            <input
              id="phone-input"
              type="tel"
              placeholder="6XX XXX XXX"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="h-11 w-full rounded-md border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition-shadow focus:border-lime-400 focus:ring-2 focus:ring-lime-400/25 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-charcoal-100"
            />
          </div>
        </div>
      )}

      {/* Prix calculé */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-700 dark:bg-charcoal-900">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm text-slate-500">Prix total</p>
            <p className="font-heading text-2xl font-bold text-charcoal-900 dark:text-charcoal-100">
              {formatFCFA(price)} <span className="text-sm font-normal text-slate-400">FCFA</span>
            </p>
          </div>
          {currentTier && (
            <p className="text-xs text-neutral-500">
              {formatFCFA(currentTier.price_per_credit)} FCFA/crédit
            </p>
          )}
        </div>
      </div>

      {/* Paliers dégressifs */}
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-charcoal-500">
          Tarifs dégressifs
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {tiers.map((tier) => (
            <button
              key={tier.min}
              type="button"
              onClick={() => setCredits(tier.min)}
              className={cn(
                'rounded-md border p-2 text-center text-xs transition-colors',
                currentTier?.min === tier.min
                  ? 'border-lime-500 bg-lime-50 dark:bg-lime-950 font-semibold text-charcoal-900'
                  : 'border-neutral-200 dark:border-charcoal-700 text-neutral-600 hover:border-neutral-300 dark:hover:border-charcoal-600',
              )}
            >
              <span className="block font-semibold">
                {tier.min}–{tier.max}
              </span>
              <span>{formatFCFA(tier.price_per_credit)} FCFA/cr.</span>
            </button>
          ))}
        </div>
      </div>

      {/* Erreur */}
      {initiatePurchase.isError && (
        <div className="rounded-md border border-error bg-error-light p-3 text-sm text-error-dark">
          Erreur lors de l&apos;initiation du paiement. Veuillez réessayer.
        </div>
      )}

      {/* Confirmation */}
      {confirming ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-700 dark:bg-charcoal-800">
          <p className="font-semibold text-charcoal-900">Confirmer l&apos;achat</p>
          <p className="mt-1 text-sm text-neutral-600">
            {credits} crédits pour <span className="font-bold">{formatFCFA(price)} FCFA</span>
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {method === 'card'
              ? 'Paiement par carte bancaire'
              : `Paiement via Mobile Money (${phoneNumber})`}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={initiatePurchase.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-lime-400 px-4 text-sm font-semibold text-charcoal-950 transition-colors hover:bg-lime-500 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/40 focus-visible:ring-offset-1"
            >
              {initiatePurchase.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  Redirection…
                </>
              ) : (
                'Payer maintenant'
              )}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={initiatePurchase.isPending}
              className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-charcoal-200 dark:hover:bg-charcoal-800"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!canPay}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-lime-400 px-4 text-sm font-semibold text-charcoal-950 transition-colors hover:bg-lime-500 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/40 focus-visible:ring-offset-1"
        >
          <Coins className="size-4" strokeWidth={2} aria-hidden />
          Acheter {credits} crédits pour {formatFCFA(price)} FCFA
        </button>
      )}

      {method === 'mobile_money' && !isPhoneValid && phoneNumber.length > 0 && (
        <p className="text-xs text-warning-dark">
          Veuillez saisir un numéro de téléphone valide (min. 8 chiffres).
        </p>
      )}

      {paymentPending && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-700 dark:bg-charcoal-800">
          {paymentStatus?.status === 'completed' ? (
            <div className="flex items-center gap-2 text-sm font-medium text-lime-700">
              <CheckCircle className="size-4" />
              Paiement confirmé ! Crédits ajoutés.
            </div>
          ) : paymentStatus?.status === 'failed' ? (
            <div className="flex items-center gap-2 text-sm font-medium text-error-dark">
              <XCircle className="size-4" />
              Paiement échoué. Vous pouvez réessayer.
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Loader2 className="size-4 animate-spin text-neutral-400" />
              <div>
                <p className="text-sm font-medium text-charcoal-900">Paiement en cours…</p>
                <p className="text-xs text-neutral-500">
                  {method === 'card'
                    ? 'Vérification du paiement en cours…'
                    : "Complétez le paiement dans l'onglet ouvert."}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AiCreditsPurchaseSheet({
  open,
  onOpenChange,
  quick = false,
  preselectedCredits,
  onPurchaseComplete,
}: AiCreditsPurchaseSheetProps) {
  const isMobile = useIsMobile();
  const [isQuickMode, setIsQuickMode] = useState(quick);

  // Reset to quick mode when re-opened with quick prop
  useEffect(() => {
    if (open) setIsQuickMode(quick);
  }, [open, quick]);

  function handleClose() {
    onOpenChange(false);
  }

  const title = isQuickMode ? 'Recharger des crédits' : 'Acheter des crédits IA';
  const description = isQuickMode
    ? 'Choisissez un pack et payez en un tap.'
    : 'Choisissez le nombre de crédits et payez via Mobile Money ou carte bancaire.';
  const TitleIcon = isQuickMode ? Zap : Coins;

  const content = isQuickMode ? (
    <QuickPurchaseContent
      onClose={handleClose}
      onSwitchToFull={() => setIsQuickMode(false)}
      preselectedCredits={preselectedCredits}
      onPurchaseComplete={onPurchaseComplete}
    />
  ) : (
    <PurchaseContent onClose={handleClose} />
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-xl">
          <SheetHeader className="pb-2">
            <SheetTitle className="flex items-center gap-2 font-heading text-lg">
              <TitleIcon className="size-5 text-lime-600" />
              {title}
            </SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="border border-slate-200 shadow-[0_24px_48px_-12px_rgba(15,23,42,0.18)] sm:max-w-md dark:border-charcoal-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg">
            <TitleIcon className="size-5 text-lime-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
